/**
 * Customer-facing promotion savings labels for flower order breakdowns (PBI #1234).
 *
 * Receipt copy follows common ecommerce patterns: name the promotion on the credit
 * line ("{headline} savings") and explain percent scope + tax relief in helper text.
 *
 * Pre-order honesty copy (PBI #1235): the sale percent is locked for any future
 * delivery date; dollars recalculate if the florist quote changes; we auto-charge
 * only when the customer total stays the same or drops.
 */

import { FlowerOrderPriceLineLabels } from '../constants/flowerOrderPriceLineLabels';
import { formatUsd } from './formatUsd';

/** Promotion discount fields used to build savings copy. */
export interface FlowerPromotionSavingsCopyInput {
  publicHeadline?: string | null;
  discountPercent?: number | null;
  productDiscountUsd?: number | null;
  taxReliefUsd?: number | null;
}

/**
 * Formats a snapshotted discount percent for receipt copy (e.g. 10 → "10%", 7.5 → "7.5%").
 */
export function formatPromotionDiscountPercent(discountPercent: number): string {
  const rounded = Number(discountPercent.toFixed(2));
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return `${text}%`;
}

/**
 * Savings line label: "{Public headline} savings", falling back to generic copy.
 */
export function buildFlowerPromotionSavingsLabel(
  publicHeadline?: string | null,
): string {
  const headline = publicHeadline?.trim();
  if (headline) {
    return `${headline} savings`;
  }

  return FlowerOrderPriceLineLabels.promotionSavingsFallback;
}

/**
 * One-line explanation under the savings row: percent scope on product + tax relief amounts.
 */
export function buildFlowerPromotionSavingsExplanation(
  input: Pick<
    FlowerPromotionSavingsCopyInput,
    'discountPercent' | 'productDiscountUsd' | 'taxReliefUsd'
  >,
): string | null {
  const { discountPercent, productDiscountUsd, taxReliefUsd } = input;
  if (
    discountPercent == null
    || productDiscountUsd == null
    || taxReliefUsd == null
    || discountPercent <= 0
    || productDiscountUsd <= 0
  ) {
    return null;
  }

  const percent = formatPromotionDiscountPercent(discountPercent);
  const productPart = `${percent} off product price (${formatUsd(productDiscountUsd)})`;

  if (taxReliefUsd > 0) {
    return `${productPart}; includes ${formatUsd(taxReliefUsd)} tax savings. Delivery fee is not discounted.`;
  }

  return `${productPart}. Delivery fee is not discounted.`;
}

/**
 * Builds label + optional explanation when a combined promotion credit is present.
 */
export function buildFlowerPromotionSavingsCopy(
  input: FlowerPromotionSavingsCopyInput,
): { label: string; explanation: string | null } {
  return {
    label: buildFlowerPromotionSavingsLabel(input.publicHeadline),
    explanation: buildFlowerPromotionSavingsExplanation(input),
  };
}

/**
 * Customer-facing helper when a pre-order carries a locked sale percent (PBI #1235).
 * Explains that the percent is locked for any future delivery date, dollars may
 * recalculate, and we only auto-charge if the total stays the same or drops.
 */
export function buildFlowerPromotionPercentLockHelper(
  discountPercent?: number | null,
): string | null {
  if (discountPercent == null || discountPercent <= 0) {
    return null;
  }

  const percent = formatPromotionDiscountPercent(discountPercent);
  return (
    `Your ${percent} sale discount is locked for this delivery date (even months out). `
    + 'If the florist\'s catalog or fees change, we recalculate your total with the same percent. '
    + 'We charge automatically only if your total stays the same or goes down — '
    + 'if you would pay more, we email you first to confirm.'
  );
}
